/**
 * DebtLedgerTable.jsx — Muhasebe FAZ 3.6 (PLAN §11.3).
 *
 * Borç (debt_rows) ve tahsilat (collection_rows) satırlarını TEK kronolojik
 * listede birleştirip DataTable (components/admin/DataTable.jsx) sarmalar.
 *
 * Kolon görünürlüğü ("Sütun Seç") ve responsive kolon gizleme kararı:
 * `DataTable` bu iki özelliği desteklemiyor ve 18 dosyada kullanımda —
 * onu genişletmek yerine bu SARMALAYICI seviyesinde çözüldü: `columnDefs.js`
 * her kolonu bir `tier` ile etiketler (required/default/optional), bu dosya
 * viewport genişliğine ve kullanıcının "Sütun Seç" seçimine göre DataTable'a
 * geçirilecek `columns` dizisini filtreler. DataTable.jsx'e DOKUNULMADI.
 *
 * <640px: DataTable yerine basit kart listesi (MobileLedgerCards) — PLAN
 * §11.3 "1024px'te sütun gizleme yatay scroll'a tercih edilir; <640px kart
 * görünümü".
 */
import { useEffect, useMemo, useState } from 'react';
import { Columns3, Receipt, Link2 } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { DataTable, EmptyState, StatusBadge } from '@/components/admin';
import { useLanguage } from '@/contexts/LanguageContext';
import { fmtMoneyAccounting } from '@/utils/money';
import {
  buildColumnDefs,
  statusBadgeProps,
  kindBadgeProps,
  InvoicePdfButton,
  reversePaymentActionCell,
  TERMINAL_DEBT_ROW_STATUSES,
} from './columnDefs';
import { ParasutStatusCell } from './parasutStatusCell';

const WIDE_BREAKPOINT = 1280;
const MOBILE_BREAKPOINT = 640;

function useViewportWidth() {
  const [width, setWidth] = useState(() => (typeof window !== 'undefined' ? window.innerWidth : 1280));
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return width;
}

function mergeRows(debtRows, collectionRows) {
  const debt = (debtRows || []).map((r) => ({ ...r, key: `debt_${r.row_type}_${r.id}` }));
  const coll = (collectionRows || []).map((r) => ({ ...r, key: `pay_${r.id}` }));
  return [...debt, ...coll].sort((a, b) => String(a.date || '').localeCompare(String(b.date || '')));
}

// ── <640px kart görünümü ───────────────────────────────────────────────────
function MobileLedgerCards({ rows, t, lang, onCollect, onReverse, onMokaLink, mokaConfig, parasutStatusMap }) {
  if (rows.length === 0) {
    return (
      <EmptyState
        icon={<Receipt size={40} strokeWidth={1.5} />}
        title={t('accounting.empty.title')}
        description={t('accounting.empty.description')}
      />
    );
  }
  return (
    <div className="space-y-2">
      {rows.map((row) => {
        const isPayment = row.row_type === 'payment';
        const status = !isPayment ? statusBadgeProps(row.status, t) : null;
        // Mobil kartta da peşinat/manuel/senet vb. ayrımı görünür olmalı —
        // masaüstündeki 'kind' kolonuyla aynı rozet, aynı slotta (status'un
        // yerini alır, çünkü tahsilat satırında zaten status yok).
        const kind = isPayment ? kindBadgeProps(row.kind, t) : null;
        const reverseCell = isPayment ? reversePaymentActionCell({ row, t, onReverse }) : null;
        return (
          <div key={row.key} className="rounded-xl border-2 border-[var(--border-light)] p-3 space-y-2 bg-white">
            <div className="flex items-center justify-between gap-2">
              <p className="font-bold text-sm truncate">{row.course_title || row.course_id || '—'}</p>
              {status && <StatusBadge variant={status.variant} label={status.label} icon={status.icon} dot />}
              {kind && <StatusBadge variant={kind.variant} label={kind.label} />}
            </div>
            {isPayment && parasutStatusMap?.[row.id]?.found && (
              <div className="flex items-center gap-1.5 text-[11px]">
                <span className="text-[var(--text-muted)] font-semibold">{t('accounting.columns.parasut_status')}:</span>
                <ParasutStatusCell paymentId={row.id} statusInfo={parasutStatusMap[row.id]} t={t} />
              </div>
            )}
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <p className="text-[var(--text-muted)]">{t('accounting.columns.debt_amount')}</p>
                <p className="font-bold tabular-nums">{fmtMoneyAccounting(!isPayment ? row.amount : 0, lang)}</p>
              </div>
              <div className="text-right">
                <p className="text-[var(--text-muted)]">{t('accounting.columns.remaining')}</p>
                <p className="font-bold tabular-nums">{fmtMoneyAccounting(!isPayment ? row.remaining : 0, lang)}</p>
              </div>
              <div>
                <p className="text-[var(--text-muted)]">{t('accounting.columns.payment_amount')}</p>
                <p className="font-bold tabular-nums">{fmtMoneyAccounting(isPayment ? row.amount : 0, lang)}</p>
              </div>
              <div className="text-right">
                <p className="text-[var(--text-muted)]">
                  {!isPayment ? t('accounting.columns.debt_date') : t('accounting.columns.payment_date')}
                </p>
                <p className="font-bold tabular-nums">{row.date || '—'}</p>
              </div>
            </div>
            {!isPayment && (row.remaining || 0) > 0 && !TERMINAL_DEBT_ROW_STATUSES.includes(row.status) && (
              <button
                type="button"
                onClick={() => onCollect(row)}
                className="w-full py-2 rounded-lg bg-emerald-500 text-white text-xs font-bold border-2 border-emerald-500 border-b-4 border-b-emerald-700"
                data-testid={`collect-row-btn-mobile-${row.key}`}
              >
                {t('accounting.actions.collect')}
              </button>
            )}
            {/* B2 fix (kriter 3) — masaüstü `action` kolonuyla (columnDefs.js)
                AYNI karar: terminal statülü (paid/cancelled) satırda kalıntı
                (remaining>0) varsa buton aktif GÖRÜNMEZ, devre dışı rozet +
                nedeni açıklayan tooltip/aria-label gösterilir. */}
            {!isPayment && (row.remaining || 0) > 0 && TERMINAL_DEBT_ROW_STATUSES.includes(row.status) && (
              <span
                className="w-full py-2 rounded-lg bg-gray-100 text-[var(--text-muted)] text-xs font-bold border-2 border-gray-200 cursor-not-allowed select-none flex items-center justify-center"
                title={t('admin.acc_collect_disabled_cancelled_hint')}
                aria-label={t('admin.acc_collect_disabled_cancelled_hint')}
                data-testid={`collect-row-btn-mobile-disabled-${row.key}`}
              >
                {t('accounting.actions.collect')}
              </span>
            )}
            {/* Faz 2 (feature/moka-profil-link, 2026-09-28) — masaüstü `action`
                kolonuyla AYNI karar (columnDefs.js): yalnızca açık TAKSİT
                satırlarında, kart(method="credit_card") BİRİNCİL geniş buton,
                havale(method="bank_transfer") ise daha az öne çıkan (neutral
                stil) ikincil buton — mobilde gerçek bir "kebab menü" için yer
                yok, bu yüzden ikincil hali stil ile ayrışır (dropdown DEĞİL). */}
            {!isPayment && row.row_type === 'installment' && onMokaLink
              && (row.remaining || 0) > 0 && !TERMINAL_DEBT_ROW_STATUSES.includes(row.status)
              && (
                mokaConfig?.link_active ? (
                  <button
                    type="button"
                    onClick={() => onMokaLink(row)}
                    className={
                      row.method === 'credit_card'
                        ? 'w-full py-2 rounded-lg bg-sky-500 text-white text-xs font-bold border-2 border-sky-500 border-b-4 border-b-sky-700 flex items-center justify-center gap-1.5'
                        : 'w-full py-2 rounded-lg bg-white text-sky-700 text-xs font-bold border-2 border-sky-200 flex items-center justify-center gap-1.5'
                    }
                    data-testid={`moka-link-btn-mobile-${row.key}`}
                  >
                    <Link2 size={13} />
                    {t('admin.moka_row_button')}
                  </button>
                ) : (
                  <span
                    className="w-full py-2 rounded-lg bg-gray-50 text-[var(--text-muted)] text-xs font-bold border-2 border-gray-200 cursor-not-allowed select-none flex items-center justify-center gap-1.5"
                    title={t('admin.moka_row_button_disabled_hint')}
                    aria-label={t('admin.moka_row_button_disabled_hint')}
                    data-testid={`moka-link-btn-mobile-disabled-${row.key}`}
                  >
                    <Link2 size={13} />
                    {t('admin.moka_row_button')}
                  </span>
                )
              )}
            {!isPayment && row.invoice_id && <InvoicePdfButton row={row} className="w-full" />}
            {reverseCell && <div className="pt-0.5">{reverseCell}</div>}
          </div>
        );
      })}
    </div>
  );
}

// ── Public ────────────────────────────────────────────────────────────────
export default function DebtLedgerTable({
  debtRows, collectionRows, loading, onCollect, onReverse, onMokaLink, mokaConfig, parasutStatusMap,
}) {
  const { t, lang } = useLanguage();
  const width = useViewportWidth();
  const isWide = width >= WIDE_BREAKPOINT;
  const isMobile = width < MOBILE_BREAKPOINT;

  const [extraCols, setExtraCols] = useState({ vat_amount: false, amount_excl_vat: false });

  const rows = useMemo(() => mergeRows(debtRows, collectionRows), [debtRows, collectionRows]);
  const allColumns = useMemo(
    () => buildColumnDefs({ t, lang, onCollect, onReverse, onMokaLink, mokaConfig, parasutStatusMap }),
    [t, lang, onCollect, onReverse, onMokaLink, mokaConfig, parasutStatusMap]
  );

  const visibleColumns = useMemo(
    () =>
      allColumns.filter((col) => {
        if (col.tier === 'default') return isWide;
        if (col.tier === 'optional') return !!extraCols[col.key];
        return true; // 'required'
      }),
    [allColumns, isWide, extraCols]
  );

  const optionalColumns = useMemo(() => allColumns.filter((c) => c.tier === 'optional'), [allColumns]);

  if (isMobile) {
    return (
      <MobileLedgerCards
        rows={rows}
        t={t}
        lang={lang}
        onCollect={onCollect}
        onReverse={onReverse}
        onMokaLink={onMokaLink}
        mokaConfig={mokaConfig}
        parasutStatusMap={parasutStatusMap}
      />
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex justify-end">
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border-2 border-[var(--border-light)] bg-white text-xs font-bold hover:bg-gray-50 transition-colors"
              data-testid="column-selector-btn"
            >
              <Columns3 size={13} />
              {t('accounting.column_selector.label')}
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-60 space-y-2 bg-white">
            {optionalColumns.map((col) => (
              <label key={col.key} className="flex items-center gap-2 text-xs font-semibold cursor-pointer">
                <Checkbox
                  checked={!!extraCols[col.key]}
                  onCheckedChange={(v) => setExtraCols((prev) => ({ ...prev, [col.key]: !!v }))}
                  aria-label={col.label}
                />
                {col.label}
              </label>
            ))}
          </PopoverContent>
        </Popover>
      </div>
      <DataTable
        columns={visibleColumns}
        data={rows}
        loading={loading}
        rowKey="key"
        emptyIcon={<Receipt size={40} strokeWidth={1.5} />}
        emptyTitle={t('accounting.empty.title')}
        emptyDescription={t('accounting.empty.description')}
      />
    </div>
  );
}
